import {
    Field,
    Formik,
} from 'formik';
import * as Yup from 'yup';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Container,
    Stack,
    Text,
} from '@mantine/core';
import {
    FormikPasswordInput,
    FormikSwitch,
    FormWithButtons,
} from '@flexinet/ui-components';

import {RevalidateFormValues} from './types';
import {ProviderConnection} from '../types';

// FNP-1248 / FNP-1269 — THE RE-VALIDATE FORM. How a tenant fixes a connection
// after editing their trust policy.
//
// ⚠️ NO PROVIDER PICKER AND NO DISPLAY NAME. Neither rotate nor revalidate has
// one, deliberately: the provider is a property of the connection, and accepting
// one would let a caller move a connection between providers under the guise of
// a credential change. The credential type is read off the connection, not
// chosen.
//
// ⚠️ FNP-1269 — THE TWO CREDENTIAL TYPES ARE NOW TWO DIFFERENT ACTS, AND THIS
// FORM ASKS FOR DIFFERENT THINGS ACCORDINGLY.
//
//   ASSUMED_ROLE — asks for NOTHING. The tenant pasted Flexi's external id into
//                  their role's trust policy and came back to say "look again".
//                  The role ARN in Secrets Manager is the same role ARN; there
//                  is no new value to collect. This form used to demand it back
//                  and NEVER prefilled it (Profile.tsx passed no `roleArn`, so
//                  the field was blank in practice, every time), which made
//                  every re-check a retype from scratch — and a typo in that
//                  retype silently repointed the connection at a DIFFERENT role.
//
//   API_TOKEN    — asks for the new token, because this genuinely IS a rotation.
//                  A token cannot be prefilled: no output type carries one,
//                  there is nothing to prefill FROM, and the tenant must paste a
//                  fresh one. The asymmetry is the schema's, not a UI choice.
//
// ⚠️ NO `writable` SWITCH ON THE ASSUMED_ROLE PATH, AND ITS ABSENCE IS
// LOAD-BEARING. `revalidateProviderConnection` PRESERVES the connection's
// existing declaration precisely by not being sent one. Re-affirming it here
// would be a revocation risk wearing a re-check's clothes: a tenant who did not
// re-tick the box would lose a capability by pressing a button that promised
// only to look again.

const revalidateSchema = (credentialType: string) => Yup.object()
                                                       .shape({
                                                           apiToken: credentialType === 'API_TOKEN'
                                                               ? Yup.string()
                                                                    .required('Enter the API token issued by your provider')
                                                               : Yup.string()
                                                                    .strip(),
                                                           writable: Yup.boolean(),
                                                       });

export const RevalidateForm = (
    {
        connection,
        handleSubmit,
        handleCancel,
    }: {
        connection: ProviderConnection,
        handleSubmit: (values: RevalidateFormValues) => Promise<unknown>,
        handleCancel?: () => void,
    },
) => {

    const isAssumedRole = connection.credentialType === 'ASSUMED_ROLE';

    // Wrapped rather than passed through: FormWithButtons types its cancel prop
    // as a MouseEventHandler, which is not optional.
    const onCancel = () => {
        handleCancel?.();
    };

    const initialValues: RevalidateFormValues = {
        credentialType: connection.credentialType,
        apiToken: '',
        // ⚠️ SEEDED FROM THE CONNECTION'S CURRENT DECLARATION, not hard-false,
        // and read only on the API_TOKEN path where the switch renders.
        //
        // FNP-1233's default-off rule governs CREATION. On a ROTATION, resetting
        // an already-writable connection to read-only because the tenant did not
        // re-tick the box would silently REVOKE a capability they never asked to
        // give up. `writable === true` is still the only thing that reads as on,
        // so an undeployed/absent field still lands false.
        writable: connection.writable === true,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={revalidateSchema(connection.credentialType)}
            onSubmit={(values: RevalidateFormValues) => handleSubmit(values)}
        >
            {({
                  handleSubmit: formikHandleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons
                    handleSubmit={formikHandleSubmit}
                    isSubmitting={isSubmitting}
                    handleCancel={onCancel}
                    // Hard-wired off: the values object holds `apiToken`, and
                    // DisplayFormikState would render it on screen.
                    debug={false}
                    {...rest}
                >
                    <Container>
                        <Stack gap={'md'}>

                            {isAssumedRole ? (
                                // ⚠️ AN EXPLANATION AND A SUBMIT BUTTON. NO FIELDS.
                                //
                                // Leaving a prefilled-but-ignored ARN input here
                                // would be worse than the bug it replaced: the
                                // tenant would edit a value that no longer
                                // travels anywhere and reasonably conclude the
                                // edit took effect.
                                <Alert color={'blue'} variant={'light'}>
                                    <Stack gap={'xs'}>
                                        <Text size={'sm'}>
                                            {productCopy('{product} will check the role it already has on file for this connection. There is nothing to re-enter.')}
                                        </Text>
                                        <Text size={'sm'}>
                                            {I18n.get('Your external ID and role ARN do not change, so the trust policy you have already written stays correct.')}
                                        </Text>
                                    </Stack>
                                </Alert>
                            ) : (
                                <>
                                    <Field
                                        name={'apiToken'}
                                        label={I18n.get('New API token')}
                                        description={productCopy('Paste a fresh token. {product} cannot show you the existing one — it is stored encrypted and is never readable back.')}
                                        component={FormikPasswordInput}
                                        withAsterisk
                                    />

                                    {/* FNP-1233 — the same declaration,
                                        re-affirmable. This is the promotion
                                        path: a connection becomes writable when
                                        the tenant declares the credential can
                                        write. It belongs to the ROTATION only —
                                        a credential-free re-check preserves the
                                        existing declaration by omitting it. */}
                                    <Field
                                        name={'writable'}
                                        label={I18n.get('This token can create and delete zones')}
                                        description={productCopy('Leave this off if you granted read-only access. {product} will not attempt any write through this connection unless you turn it on.')}
                                        component={FormikSwitch}
                                    />
                                </>
                            )}

                        </Stack>
                    </Container>
                </FormWithButtons>
            )}
        </Formik>
    );
};
